import { useMemo, useState, type ImgHTMLAttributes } from 'react';
import { isPresignedS3Url, useResolvedMediaUrl } from '@/lib/mediaUrl';
import { externalImageProps } from '@/lib/externalImage';
import { IMAGE_DIMENSIONS, optimizeRemoteImageUrl, preferWebpVariantUrl } from '@/lib/imageUrl';

type DimensionPreset = keyof typeof IMAGE_DIMENSIONS;

/** Renders S3 storage paths (`public/...`) and https/data URLs for product/media images. */
export function MediaImage({
  src,
  alt = '',
  className,
  onError,
  dimensionPreset,
  width,
  height,
  ...rest
}: ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  /** Sets width/height hints when omitted (reduces CLS). */
  dimensionPreset?: DimensionPreset;
}) {
  const resolved = useResolvedMediaUrl(src);
  const [failed, setFailed] = useState(false);
  const preset = dimensionPreset ? IMAGE_DIMENSIONS[dimensionPreset] : null;
  const w = width ?? preset?.width;
  const h = height ?? preset?.height;
  const displaySrc = useMemo(() => {
    if (!resolved) return undefined;
    const base =
      resolved.startsWith('http') && !isPresignedS3Url(resolved)
        ? preferWebpVariantUrl(resolved)
        : resolved;
    if (typeof w === 'number' && base.startsWith('http') && !isPresignedS3Url(base)) {
      return optimizeRemoteImageUrl(base, w);
    }
    return base;
  }, [resolved, w]);

  if (failed) return null;

  if (!displaySrc) {
    if (typeof w === 'number' && typeof h === 'number') {
      return (
        <div
          className={className}
          style={{ width: w, height: h, aspectRatio: `${w} / ${h}` }}
          aria-hidden
        />
      );
    }
    return null;
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
      width={w}
      height={h}
      decoding="async"
      onError={(e) => {
        setFailed(true);
        onError?.(e);
      }}
      {...externalImageProps(displaySrc)}
      {...rest}
    />
  );
}
